import React from 'react';
import { Globe, MessageSquare, Zap, Bot, CheckCircle2 } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
export function HowItWorks() {
  const {
    ref: sectionRef,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  return <section id="how-it-works" className="bg-slate-900/50 py-24 border-y border-slate-800" data-id="element-207">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={sectionRef} data-id="element-208">
        <div className={`text-center mb-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} data-id="element-209">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-100 tracking-tight" data-id="element-210">
            From question to action in seconds
          </h2>
        </div>

        {/* 3 Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative mb-24" data-id="element-211">
          {/* Connecting lines (desktop only) */}
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-px border-t-2 border-dashed border-slate-700 z-0" data-id="element-212"></div>

          {/* Step 1 */}
          <div className={`relative z-10 flex flex-col items-center text-center ${isVisible ? 'animate-fade-in-up delay-100' : 'opacity-0'}`} data-id="element-213">
            <div className="w-24 h-24 rounded-2xl bg-slate-900 border border-slate-700 flex items-center justify-center mb-6 shadow-xl relative" data-id="element-214">
              <span className="absolute -top-4 -left-4 text-6xl font-black bg-gradient-to-br from-indigo-500 to-sky-400 bg-clip-text text-transparent opacity-50" data-id="element-215">
                1
              </span>
              <Globe className="w-10 h-10 text-indigo-400" data-id="element-216" />
            </div>
            <h3 className="text-xl font-bold text-slate-100 mb-2" data-id="element-217">
              Connect your tools
            </h3>
            <p className="text-slate-400 max-w-xs" data-id="element-218">
              Link Jira, Slack, Confluence, and more in one click.
            </p>
          </div>

          {/* Step 2 */}
          <div className={`relative z-10 flex flex-col items-center text-center ${isVisible ? 'animate-fade-in-up delay-300' : 'opacity-0'}`} data-id="element-219">
            <div className="w-24 h-24 rounded-2xl bg-slate-900 border border-slate-700 flex items-center justify-center mb-6 shadow-xl relative" data-id="element-220">
              <span className="absolute -top-4 -left-4 text-6xl font-black bg-gradient-to-br from-indigo-500 to-sky-400 bg-clip-text text-transparent opacity-50" data-id="element-221">
                2
              </span>
              <MessageSquare className="w-10 h-10 text-indigo-400" data-id="element-222" />
            </div>
            <h3 className="text-xl font-bold text-slate-100 mb-2" data-id="element-223">
              Ask or assign
            </h3>
            <p className="text-slate-400 max-w-xs" data-id="element-224">
              Talk to Workium AI like a teammate. Ask questions or delegate
              tasks.
            </p>
          </div>

          {/* Step 3 */}
          <div className={`relative z-10 flex flex-col items-center text-center ${isVisible ? 'animate-fade-in-up delay-500' : 'opacity-0'}`} data-id="element-225">
            <div className="w-24 h-24 rounded-2xl bg-slate-900 border border-slate-700 flex items-center justify-center mb-6 shadow-xl relative" data-id="element-226">
              <span className="absolute -top-4 -left-4 text-6xl font-black bg-gradient-to-br from-indigo-500 to-sky-400 bg-clip-text text-transparent opacity-50" data-id="element-227">
                3
              </span>
              <Zap className="w-10 h-10 text-indigo-400" data-id="element-228" />
            </div>
            <h3 className="text-xl font-bold text-slate-100 mb-2" data-id="element-229">
              AI acts & reports
            </h3>
            <p className="text-slate-400 max-w-xs" data-id="element-230">
              Get answers, updates, and actions — across every connected tool.
            </p>
          </div>
        </div>

        {/* Mock Chat UI */}
        <div className={`max-w-3xl mx-auto bg-slate-900 rounded-2xl border border-slate-800 p-6 shadow-2xl ${isVisible ? 'animate-fade-in-up delay-700' : 'opacity-0'}`} data-id="element-231">
          <div className="flex items-center gap-3 mb-8 pb-4 border-b border-slate-800" data-id="element-232">
            <div className="w-3 h-3 rounded-full bg-rose-500" data-id="element-233"></div>
            <div className="w-3 h-3 rounded-full bg-amber-500" data-id="element-234"></div>
            <div className="w-3 h-3 rounded-full bg-emerald-500" data-id="element-235"></div>
            <span className="ml-4 text-sm font-medium text-slate-500" data-id="element-236">
              Workium AI Assistant
            </span>
          </div>

          <div className="space-y-6" data-id="element-237">
            {/* User Message */}
            <div className="flex justify-end" data-id="element-238">
              <div className="bg-indigo-600/10 border border-indigo-500/20 text-indigo-100 rounded-2xl rounded-tr-sm px-5 py-3 max-w-[80%]" data-id="element-239">
                What's blocking Sprint 42?
              </div>
            </div>

            {/* Bot Message */}
            <div className="flex gap-4" data-id="element-240">
              <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-241">
                <Bot className="w-6 h-6 text-white" data-id="element-242" />
              </div>
              <div className="bg-slate-800 border border-slate-700 text-slate-300 rounded-2xl rounded-tl-sm px-5 py-4 max-w-[85%]" data-id="element-243">
                <p className="mb-3" data-id="element-244">
                  3 high-priority issues without linked PRs:{' '}
                  <span className="text-indigo-400 font-mono text-sm" data-id="element-245">
                    PROJ-123
                  </span>
                  ,{' '}
                  <span className="text-indigo-400 font-mono text-sm" data-id="element-246">
                    PROJ-126
                  </span>
                  ,{' '}
                  <span className="text-indigo-400 font-mono text-sm" data-id="element-247">
                    PROJ-128
                  </span>
                  .
                </p>
                <p data-id="element-248">
                  <span className="text-indigo-400 font-mono text-sm" data-id="element-249">
                    PROJ-102
                  </span>{' '}
                  is blocking two of them. Want me to ping the assignee?
                </p>
              </div>
            </div>

            {/* User Message */}
            <div className="flex justify-end" data-id="element-250">
              <div className="bg-indigo-600/10 border border-indigo-500/20 text-indigo-100 rounded-2xl rounded-tr-sm px-5 py-3 max-w-[80%]" data-id="element-251">
                Yes, do it.
              </div>
            </div>

            {/* Bot Message */}
            <div className="flex gap-4" data-id="element-252">
              <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-253">
                <Bot className="w-6 h-6 text-white" data-id="element-254" />
              </div>
              <div className="bg-slate-800 border border-slate-700 text-slate-300 rounded-2xl rounded-tl-sm px-5 py-4 max-w-[85%] flex items-start gap-3" data-id="element-255">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" data-id="element-256" />
                <p data-id="element-257">
                  Notification sent to{' '}
                  <span className="text-sky-400" data-id="element-258">@alice</span> via Slack. I've
                  also added a comment on{' '}
                  <span className="text-indigo-400 font-mono text-sm" data-id="element-259">
                    PROJ-102
                  </span>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}